import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, TrendingUp, Table, Beaker, FileCheck, Lightbulb, CheckCircle, AlertCircle, Activity, FlaskConical, Database, Waves, BarChart3, Sparkles, Microscope, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, ReferenceDot, AreaChart, Area, ComposedChart, Scatter
} from "recharts";
import ReproducibleAIBadge from "@/components/ReproducibleAIBadge";
import FtirReportSections from "@/components/FtirReportSections";
import { InstrumentParamsBox } from "@/components/InstrumentParams";
import UspCorrectiveActions from "@/components/UspCorrectiveActions";
import { logger } from '@/lib/logger';
import "./DemoPage.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mirror the helpers used by DemoPage.js so the Analysis Report renders the
// same research-grade sections from the shared backend payload.
const isChromatography = (t) =>
  t === "gc-chromatogram" || t === "hplc-chromatogram" || t?.includes("chromatogram");

const getUnits = (t) => {
  if (isChromatography(t)) return { position: "min", intensity: "mAU" };
  if (t === "ftir-spectrum") return { position: "cm⁻¹", intensity: "%T" };
  if (t === "uv-vis-spectrum" || t === "nir-spectrum") return { position: "nm", intensity: "Abs" };
  return { position: "", intensity: "" };
};

const confidenceTier = (c = "") => {
  const n = typeof c === "number" ? c : parseFloat(c);
  if (!Number.isNaN(n) && Number.isFinite(n)) {
    if (n >= 80) return "high";
    if (n >= 50) return "medium";
    return "low";
  }
  const v = String(c).toLowerCase();
  if (v.includes("high")) return "high";
  if (v.includes("med"))  return "medium";
  return "low";
};

const formatConfidence = (obj) => {
  if (!obj) return "—";
  const n = obj.match_confidence_percent;
  if (typeof n === "number" && Number.isFinite(n)) return `${Math.round(n)}%`;
  if (obj.confidence) return obj.confidence;
  return "—";
};

const AnalysisPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchAnalysis();
  }, [id]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/analyses/${id}`, {
        withCredentials: true,
      });
      setAnalysis(response.data);
    } catch (error) {
      toast.error("Failed to load analysis");
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      const response = await axios.post(
        `${API}/generate-pdf/${id}`,
        {},
        { responseType: 'blob', withCredentials: true }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF report downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF");
      logger.error(error);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-600">Analysis not found</p>
      </div>
    );
  }

  // Prepare chart data
  const chartData = analysis.chart_data
    ? analysis.chart_data.x.map((x, i) => ({
        x: x,
        y: analysis.chart_data.y[i],
      }))
    : [];

  // Running counter for contiguous section numbering (increments only for
  // sections that actually render, since each call sits inside its own
  // conditional block).
  let sectionNum = 0;
  const nextSectionNum = () => ++sectionNum;

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <header className="glass-card border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Button
            data-testid="download-pdf-button"
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'Downloading...' : 'Download PDF'}
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Title Section */}
        <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-slate-900">
                  Analysis Report
                </h1>
                <ReproducibleAIBadge />
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span data-testid="file-name">{analysis.file_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  <span data-testid="analysis-type">{analysis.analysis_type}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <p className="text-sm text-blue-800">
              Generated on {new Date(analysis.timestamp).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Chart Section - Dark Style Like Landing Demo */}
        {chartData.length > 0 && (
          <div className="bg-slate-800 rounded-2xl p-6 mb-8 fade-in border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-cyan-400" />
                <h2 className="text-xl font-bold text-white">Spectral Visualization</h2>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 text-xs rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                  {analysis.analysis_type?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                {analysis.chart_data?.peaks?.length > 0 && (
                  <span className="px-3 py-1 text-xs rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                    {analysis.chart_data.peaks.length} peaks detected
                  </span>
                )}
              </div>
            </div>

            <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                  <defs>
                    <linearGradient id="colorSpectrum" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="x"
                    stroke="#64748b"
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    label={{ 
                      value: analysis.chart_data?.xlabel || 'X-Axis', 
                      position: 'insideBottom', 
                      offset: -10,
                      fill: '#94a3b8',
                      fontSize: 11
                    }}
                    reversed={analysis.analysis_type === 'ftir-spectrum'}
                  />
                  <YAxis
                    stroke="#64748b"
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    label={{ 
                      value: analysis.chart_data?.ylabel || 'Y-Axis', 
                      angle: -90, 
                      position: 'insideLeft',
                      fill: '#94a3b8',
                      fontSize: 11
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(30, 41, 59, 0.95)',
                      border: '1px solid #475569',
                      borderRadius: '8px',
                      color: '#e2e8f0'
                    }}
                    formatter={(value) => [typeof value === 'number' ? value.toFixed(3) : value]}
                    labelFormatter={(label) => `${analysis.chart_data?.xlabel}: ${typeof label === 'number' ? label.toFixed(2) : label}`}
                  />
                  
                  {/* Area fill under the curve */}
                  <Area
                    type="monotone"
                    dataKey="y"
                    stroke="none"
                    fill="url(#colorSpectrum)"
                  />
                  
                  {/* Main spectrum line */}
                  <Line
                    type="monotone"
                    dataKey="y"
                    stroke="#06b6d4"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: '#06b6d4', stroke: '#fff' }}
                  />
                  
                  {/* Peak markers as dashed reference lines */}
                  {analysis.chart_data?.peaks?.map((peak, index) => (
                    <ReferenceLine
                      key={`peak-line-${index}`}
                      x={peak.x}
                      stroke="#ef4444"
                      strokeDasharray="4 4"
                      strokeWidth={2}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
              
              {/* Peak Labels - Inline Style Like Demo */}
              {analysis.chart_data?.peaks?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4 justify-center">
                  {analysis.chart_data.peaks.map((peak, index) => {
                    // Assign colors to different peaks
                    const colors = ['#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];
                    const color = colors[index % colors.length];
                    
                    // Get functional group name if available
                    const groupName = peak.group || (
                      analysis.functional_groups?.[index] || 
                      `Peak ${index + 1}`
                    );
                    
                    return (
                      <div 
                        key={index}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm"
                        style={{ backgroundColor: `${color}20`, border: `1px solid ${color}40` }}
                      >
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }}></div>
                        <span className="text-white font-medium">{peak.label}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-slate-300">{groupName}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ RESEARCH-GRADE RICH REPORT SECTIONS ══════════════════════════
            Mirrors the DemoPage ResultView: executive summary, material ID,
            FTIR 1→5, HPLC quantitative table, chromatographic conditions,
            detailed peaks, functional groups (rich cards), compound ID,
            publication paragraph, user guidance. Wrapped in `demo-root` so
            the dark-theme CSS variables cascade only to these blocks and
            don't leak into the legacy light sections below.
            ================================================================ */}
        {(analysis.executive_summary ||
          analysis.material_inference ||
          analysis.ftir_report ||
          analysis.hplc_metrics?.length > 0 ||
          analysis.chromatographic_conditions ||
          analysis.peak_analysis_detailed?.length > 0 ||
          analysis.functional_groups_identified?.length > 0 ||
          analysis.compound_identification?.length > 0 ||
          analysis.publication_paragraph ||
          analysis.user_guidance?.length > 0) && (() => {
          const units = getUnits(analysis.analysis_type);
          const isChromo = isChromatography(analysis.analysis_type);
          return (
            <div className="demo-root rounded-2xl mb-8 fade-in" style={{ background: 'var(--navy)', padding: '24px' }} data-testid="analysis-rich-sections">
              {/* Baseline-correction warning — rendered first so the user
                  sees the data-quality issue before trusting any of the
                  quantitative numbers below. */}
              {analysis.baseline_warning && (
                <div
                  className="demo-result-block"
                  data-testid="baseline-warning-banner"
                  style={{
                    background: "rgba(239, 68, 68, 0.08)",
                    border: "1px solid rgba(239, 68, 68, 0.45)",
                    borderLeft: "4px solid #dc2626",
                    borderRadius: 8,
                    padding: "12px 16px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <AlertCircle size={16} color="#dc2626" />
                    <span style={{ color: "#dc2626", fontSize: 13, fontWeight: 700, letterSpacing: 0.3 }}>
                      Baseline correction required
                    </span>
                  </div>
                  <div style={{ color: "var(--text2)", fontSize: 12.5, lineHeight: 1.5 }}>
                    {analysis.baseline_warning}
                  </div>
                </div>
              )}

              {/* Executive Summary */}
              {analysis.executive_summary && (
                <div className="demo-result-block">
                  <div className="demo-section-h">
                    <FileText size={11} /> Executive Summary
                  </div>
                  <div className="demo-summary-box">
                    <p>{analysis.executive_summary}</p>
                  </div>
                </div>
              )}

              {/* Material Identification */}
              {analysis.material_inference?.predicted_material_class && (
                <div className="demo-result-block">
                  <div className="demo-section-h">
                    <Beaker size={11} /> Material Identification
                  </div>
                  <div className="demo-material-card">
                    <div className="demo-material-head">
                      <span className="demo-material-class">
                        {analysis.material_inference.predicted_material_class}
                      </span>
                      {analysis.material_inference.confidence && (
                        <span className={`demo-confidence-pill ${confidenceTier(analysis.material_inference.confidence)}`}>
                          {analysis.material_inference.confidence}
                        </span>
                      )}
                    </div>
                    {analysis.material_inference.supporting_evidence?.length > 0 && (
                      <div className="demo-evidence-list">
                        {analysis.material_inference.supporting_evidence.slice(0, 3).map((e, i) => (
                          <div key={`ev-${i}-${(e || "").slice(0, 24)}`}>• {e}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <InstrumentParamsBox rows={analysis.instrument_parameters} />

              {/* FTIR Report (Sections 1-5) */}
              {analysis.ftir_report && (
                <FtirReportSections report={analysis.ftir_report} />
              )}

              {/* HPLC / GC quantitative report */}
              {analysis.hplc_metrics?.length > 0 && (
                <div className="demo-result-block">
                  <div className="demo-section-h blue">
                    <BarChart3 size={11} /> Quantitative Peak Report (USP / Ph. Eur.)
                  </div>

                  {/* USP <621> System Suitability banner */}
                  {analysis.system_suitability && (
                    <div
                      data-testid="system-suitability-banner"
                      data-status={analysis.system_suitability.status?.toLowerCase()}
                      style={{
                        marginBottom: 14,
                        padding: "12px 14px",
                        borderRadius: 6,
                        border: `1px solid ${
                          analysis.system_suitability.all_pass
                            ? "rgba(34,197,94,0.45)"
                            : analysis.system_suitability.status === "Fail"
                            ? "rgba(239,68,68,0.45)"
                            : "rgba(234,179,8,0.45)"
                        }`,
                        background: analysis.system_suitability.all_pass
                          ? "rgba(34,197,94,0.06)"
                          : analysis.system_suitability.status === "Fail"
                          ? "rgba(239,68,68,0.06)"
                          : "rgba(234,179,8,0.06)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        <span
                          style={{
                            fontFamily: "var(--mono)",
                            fontSize: 10,
                            letterSpacing: 1,
                            fontWeight: 700,
                            padding: "3px 8px",
                            borderRadius: 4,
                            color: "#fff",
                            background: analysis.system_suitability.all_pass
                              ? "#16a34a"
                              : analysis.system_suitability.status === "Fail"
                              ? "#dc2626"
                              : "#ca8a04",
                          }}
                          data-testid="system-suitability-status"
                        >
                          {analysis.system_suitability.all_pass ? "PASS" : analysis.system_suitability.status?.toUpperCase()}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>
                          USP &lt;621&gt; System Suitability
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 10 }}>
                        {analysis.system_suitability.summary}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
                        {(analysis.system_suitability.criteria || []).map((c, idx) => (
                          <div
                            key={`crit-${idx}`}
                            data-testid={`suitability-criterion-${idx}`}
                            style={{
                              padding: "6px 8px",
                              borderRadius: 4,
                              background: "rgba(255,255,255,0.04)",
                              borderLeft: `3px solid ${
                                c.pass === true ? "#16a34a" : c.pass === false ? "#dc2626" : "#9ca3af"
                              }`,
                            }}
                          >
                            <div style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                              {c.name}
                            </div>
                            <div style={{ fontSize: 12, color: "var(--text2)", fontFamily: "var(--mono)", marginTop: 2 }}>
                              {c.observed} <span style={{ color: "var(--text3)" }}>(req. {c.threshold})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <UspCorrectiveActions suitability={analysis.system_suitability} />
                    </div>
                  )}

                  <div style={{ overflowX: "auto" }}>
                    <table className="demo-table" data-testid="hplc-metrics-table">
                      <thead>
                        <tr>
                          <th>Peak</th>
                          <th>Rt (min)</th>
                          <th>Area (mAU·min)</th>
                          <th>Height (mAU)</th>
                          <th>% Area</th>
                          <th>W½ (min)</th>
                          <th>As</th>
                          <th>N (plates)</th>
                          <th>Rs vs prev.</th>
                          <th>Compound</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysis.hplc_metrics.map((p, i) => {
                          const isUnknown = (p.compound_label || "").startsWith("Unknown");
                          const N = p.theoretical_plates;
                          const As = p.symmetry_factor;
                          const Rs = p.resolution_to_previous;
                          return (
                            <tr key={`hplc-${i}`} data-testid={`hplc-peak-row-${i}`}>
                              <td><span className="demo-mono">{i + 1}</span></td>
                              <td><span className="demo-mono">{p.retention_time?.toFixed(3)}</span></td>
                              <td><span className="demo-mono">{p.area?.toFixed(2)}</span></td>
                              <td><span className="demo-mono">{p.height?.toFixed(2) ?? "—"}</span></td>
                              <td><span className="demo-mono">{p.relative_area?.toFixed(2)} %</span></td>
                              <td><span className="demo-mono">{p.width_half_height?.toFixed(4) ?? "—"}</span></td>
                              <td><span className="demo-mono">{As?.toFixed(3) ?? "—"}</span></td>
                              <td><span className="demo-mono">{N ?? "—"}</span></td>
                              <td><span className="demo-mono">{Rs?.toFixed(2) ?? "—"}</span></td>
                              <td style={{ fontSize: 12, color: isUnknown ? "var(--text3)" : "var(--text)", fontStyle: isUnknown ? "italic" : "normal" }}>
                                {p.compound_label}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Chromatographic conditions */}
              {analysis.chromatographic_conditions && (
                <div className="demo-result-block">
                  <div className="demo-section-h">
                    <FlaskConical size={11} /> Chromatographic Conditions
                  </div>
                  <div className="demo-chart-card" data-testid="chromatographic-conditions">
                    {analysis.chromatographic_conditions.is_default && (
                      <div style={{
                        padding: "10px 12px",
                        marginBottom: 12,
                        background: "rgba(251, 191, 36, 0.08)",
                        border: "1px solid rgba(251, 191, 36, 0.22)",
                        borderRadius: 8,
                        fontSize: 12,
                        color: "var(--warn)"
                      }}>
                        ⚠ {analysis.chromatographic_conditions.note || "Defaults shown — please edit before publishing."}
                      </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                      {Object.entries(analysis.chromatographic_conditions)
                        .filter(([k]) => !["is_default", "note"].includes(k))
                        .map(([key, value]) => {
                          const label = key
                            .replace(/_/g, " ")
                            .replace(/\bml min\b/i, "(mL/min)")
                            .replace(/\bnm\b/i, "(nm)")
                            .replace(/\bc\b/i, "(°C)")
                            .replace(/\bul\b/i, "(µL)");
                          return (
                            <div key={key} style={{
                              background: "var(--navy3)",
                              border: "1px solid var(--border)",
                              borderRadius: 8,
                              padding: "10px 12px"
                            }}>
                              <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: "var(--mono)" }}>
                                {label}
                              </div>
                              <div style={{ fontSize: 13, color: value ? "var(--text)" : "var(--text3)" }}>
                                {value || "— not provided —"}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}

              {/* Detailed Peak Analysis (suppressed for FTIR — FTIR has its own table) */}
              {analysis.peak_analysis_detailed?.length > 0 && !analysis.ftir_report && (
                <div className="demo-result-block">
                  <div className="demo-section-h blue">
                    <BarChart3 size={11} /> Detailed Peak Analysis
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table className="demo-table">
                      <thead>
                        <tr>
                          <th>{isChromo ? "Retention time" : "Position"}</th>
                          <th>{isChromo ? "Area / Height" : "Intensity"}</th>
                          <th>{isChromo ? "Compound" : "Assignment"}</th>
                          <th>Scientific interpretation</th>
                          <th>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysis.peak_analysis_detailed.slice(0, 8).map((peak, i) => {
                          const position = peak.position_cm || peak.position || peak.retention_time;
                          return (
                            <tr key={`pad-${position ?? i}-${peak.assignment ?? peak.compound ?? ""}`}>
                              <td><span className="demo-mono">{position} {units.position}</span></td>
                              <td>{peak.intensity || peak.area}</td>
                              <td>
                                <span style={{ color: "var(--text)", fontWeight: 500 }}>
                                  {peak.assignment || peak.compound}
                                </span>
                                {peak.bond_type && (
                                  <span style={{ display: "block", color: "var(--text3)", fontSize: 11, marginTop: 2 }}>
                                    {peak.bond_type}
                                  </span>
                                )}
                              </td>
                              <td style={{ fontSize: 11.5, color: "var(--text3)", maxWidth: 320 }}>
                                {peak.interpretation || (peak.literature_range && `Literature: ${peak.literature_range}`)}
                              </td>
                              <td>
                                <span className={`demo-confidence-pill ${confidenceTier(peak.match_confidence_percent ?? peak.confidence)}`}>
                                  {formatConfidence(peak)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Functional Groups (rich card format) */}
              {analysis.functional_groups_identified?.length > 0 && (
                <div className="demo-result-block">
                  <div className="demo-section-h purple">
                    <FlaskConical size={11} /> Functional Groups Identified
                  </div>
                  <div className="demo-groups-grid">
                    {analysis.functional_groups_identified.slice(0, 6).map((g, i) => (
                      <div key={`fg-${g.group ?? i}`} className="demo-group-card">
                        <div className="demo-group-card-top">
                          <span className="demo-group-name">{g.group}</span>
                          {g.confidence && (
                            <span className={`demo-confidence-pill ${confidenceTier(g.confidence)}`}>
                              {g.confidence}
                            </span>
                          )}
                        </div>
                        {g.evidence_peaks && (
                          <div className="demo-group-meta">Evidence: {Array.isArray(g.evidence_peaks) ? g.evidence_peaks.join(", ") : g.evidence_peaks}</div>
                        )}
                        {g.structural_role && (
                          <div className="demo-group-meta">{g.structural_role}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Compound Identification (rich format, mirrors DemoPage) */}
              {analysis.compound_identification?.length > 0 && (
                <div className="demo-result-block">
                  <div className="demo-section-h amber">
                    <Database size={11} /> Compound Identification
                  </div>
                  <div className="demo-compound-list">
                    {analysis.compound_identification.slice(0, 5).map((c, i) => {
                      const isString = typeof c === "string";
                      const name = isString ? c : (c.compound_name || c.name || "Unknown");
                      const conf = isString ? null : (c.confidence || c.confidence_score);
                      const matching = isString ? null : c.matching_peaks;
                      const rt = isString ? null : (c.retention_time || c.rt);
                      return (
                        <div key={`cmpd-${name}-${rt ?? i}`} className="demo-compound-row">
                          <span className="demo-compound-rank">{i + 1}.</span>
                          <div className="demo-compound-body">
                            <span className="demo-compound-name-l">{name}</span>
                            {conf && (
                              <span className={`demo-confidence-pill ${confidenceTier(conf)}`}>{conf}</span>
                            )}
                            {isChromo && rt && (
                              <div className="demo-compound-meta">Retention time: {rt} min</div>
                            )}
                            {!isChromo && matching?.length > 0 && (
                              <div className="demo-compound-meta">
                                Matching peaks: {Array.isArray(matching) ? matching.join(", ") : matching}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Publication Paragraph */}
              {analysis.publication_paragraph && (
                <div className="demo-result-block">
                  <div className="demo-section-h green">
                    <FileText size={11} /> Publication-Ready Paragraph
                  </div>
                  <div className="demo-publication-box">
                    <p>&ldquo;{analysis.publication_paragraph}&rdquo;</p>
                    <div className="demo-publication-foot">
                      Formatted for the Results / Discussion section of scientific papers.
                    </div>
                  </div>
                </div>
              )}

              {/* Research Guidance & Insights */}
              {analysis.user_guidance?.length > 0 && (
                <div className="demo-result-block">
                  <div className="demo-section-h amber">
                    <Wand2 size={11} /> Research Guidance &amp; Insights
                  </div>
                  <div className="demo-guidance-list">
                    {analysis.user_guidance.slice(0, 4).map((g, i) => (
                      <div key={`guidance-${i}-${(g || "").slice(0, 24)}`} className="demo-guidance-item">
                        <Sparkles size={14} />
                        <span>{g}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Results Table */}
        {analysis.results_table && analysis.results_table.headers && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-6">
              <Table className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Results Table</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-600">
                  <tr>
                    {analysis.results_table.headers.map((header, index) => (
                      <th
                        key={index}
                        className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analysis.results_table.rows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* General Interpretation — legacy fallback when `executive_summary` is empty */}
        {!analysis.executive_summary && analysis.interpretation && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. General Interpretation</h2>
            </div>
            <div className="prose max-w-none">
              <p data-testid="interpretation-text" className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                {analysis.interpretation}
              </p>
            </div>
          </div>
        )}

        {/* Peak Analysis — legacy fallback (string list) when `peak_analysis_detailed` is empty */}
        {!(analysis.peak_analysis_detailed?.length > 0) && analysis.peak_analysis && analysis.peak_analysis.length > 0 && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-6">
              <Activity className="w-6 h-6 text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Peak Analysis</h2>
            </div>
            <div className="space-y-3">
              {analysis.peak_analysis.map((peak, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-cyan-50 rounded-xl border border-cyan-200"
                >
                  <div className="bg-cyan-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                    <Waves className="w-4 h-4" />
                  </div>
                  <p className="text-slate-700 pt-1">{peak}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Functional Groups — legacy fallback (string pills) when `functional_groups_identified` is empty */}
        {!(analysis.functional_groups_identified?.length > 0) && analysis.functional_groups && analysis.functional_groups.length > 0 && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-6">
              <FlaskConical className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Functional Groups Identified</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {analysis.functional_groups.map((group, index) => (
                <span
                  key={index}
                  className="px-4 py-2 rounded-full text-sm font-medium bg-purple-100 text-purple-700 border border-purple-200"
                >
                  {group}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Possible Compounds / Database Comparison — legacy fallback when `compound_identification` is empty */}
        {!(analysis.compound_identification?.length > 0) && analysis.possible_compounds && analysis.possible_compounds.length > 0 && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-6">
              <Database className="w-6 h-6 text-amber-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Possible Compounds (Database Match)</h2>
            </div>
            {analysis.database_comparison && (
              <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-slate-700">{analysis.database_comparison}</p>
              </div>
            )}
            <div className="space-y-3">
              {analysis.possible_compounds.map((compound, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200"
                >
                  <div className="bg-amber-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-slate-700 pt-1">{compound}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NOTE: The legacy duplicate "Compound Identification" block was
            removed — the richer version now renders inside the demo-root
            rich-sections block above (reads from analysis.compound_identification
            with confidence pills, retention time, matching peaks). */}

        {/* Quantification Table (for Chromatography) - NEW */}
        {analysis.quantification && analysis.quantification.headers && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-6">
              <Table className="w-6 h-6 text-teal-600" />
              <h2 className="text-2xl font-bold text-slate-900">Quantification Results</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-teal-600">
                  <tr>
                    {analysis.quantification.headers.map((header, index) => (
                      <th
                        key={index}
                        className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analysis.quantification.rows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Chemical Interpretation */}
        {analysis.chemical_interpretation && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <Beaker className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Chemical Interpretation</h2>
            </div>
            <div className="prose max-w-none">
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                {analysis.chemical_interpretation}
              </p>
            </div>
          </div>
        )}

        {/* Specifications Comparison */}
        {analysis.specifications_comparison && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <FileCheck className="w-6 h-6 text-green-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Comparison with Specifications</h2>
            </div>
            <div className="prose max-w-none">
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                {analysis.specifications_comparison}
              </p>
            </div>
          </div>
        )}

        {/* Technical Justifications */}
        {analysis.technical_justifications && analysis.technical_justifications.length > 0 && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-6">
              <Lightbulb className="w-6 h-6 text-yellow-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Technical Justifications</h2>
            </div>
            <div className="space-y-4">
              {analysis.technical_justifications.map((justification, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 bg-yellow-50 rounded-xl border border-yellow-200"
                >
                  <div className="bg-yellow-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-slate-700 pt-1">{justification}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conclusions */}
        {analysis.conclusions && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Conclusions</h2>
            </div>
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded">
              {typeof analysis.conclusions === 'string' ? (
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {analysis.conclusions}
                </p>
              ) : (
                <div className="space-y-4">
                  {analysis.conclusions.main_finding && (
                    <p className="text-slate-900 font-medium text-lg">
                      {analysis.conclusions.main_finding}
                    </p>
                  )}
                  {analysis.conclusions.secondary_findings && analysis.conclusions.secondary_findings.length > 0 && (
                    <ul className="list-disc list-inside space-y-1 text-slate-700">
                      {analysis.conclusions.secondary_findings.map((finding, i) => (
                        <li key={i}>{finding}</li>
                      ))}
                    </ul>
                  )}
                  {analysis.conclusions.confidence_statement && (
                    <p className="text-sm text-slate-600 italic border-t border-green-200 pt-3 mt-3">
                      {analysis.conclusions.confidence_statement}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {analysis.recommendations && (
          <div className="glass-card rounded-2xl p-8 mb-8 fade-in">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-6 h-6 text-orange-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Recommendations</h2>
            </div>
            {Array.isArray(analysis.recommendations) ? (
              /* Array of strings format (chromatography) */
              <div className="space-y-4">
                {analysis.recommendations.map((recommendation, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-orange-50 rounded-xl border border-orange-200"
                  >
                    <div className="bg-orange-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                      {index + 1}
                    </div>
                    <p className="text-slate-700 pt-1">{typeof recommendation === 'string' ? recommendation : JSON.stringify(recommendation)}</p>
                  </div>
                ))}
              </div>
            ) : (
              /* Object format (spectroscopy) */
              <div className="space-y-6">
                {analysis.recommendations.further_analysis && analysis.recommendations.further_analysis.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Further Analysis Recommended
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                      {analysis.recommendations.further_analysis.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.recommendations.sample_handling && analysis.recommendations.sample_handling.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Sample Handling
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                      {analysis.recommendations.sample_handling.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {analysis.recommendations.quality_control && analysis.recommendations.quality_control.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Quality Control
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-slate-700 ml-4">
                      {analysis.recommendations.quality_control.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Key Insights — legacy fallback when `user_guidance` is empty */}
        {!(analysis.user_guidance?.length > 0) && analysis.insights && analysis.insights.length > 0 && (
          <div className="glass-card rounded-2xl p-8 fade-in">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">{nextSectionNum()}. Key Insights</h2>
            </div>
            <div className="space-y-4">
              {analysis.insights.map((insight, index) => (
                <div
                  key={index}
                  data-testid={`insight-${index}`}
                  className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100"
                >
                  <div className="bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-slate-700 pt-1">{insight}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisPage;